import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

// Idea from https://github.com/angular/angular/issues/18155, https://gist.github.com/dawidgarus/6fd4ae338334ba65fd5b37e1a74bb13e
@Injectable({ providedIn: 'root' })
export class ScgHttpWrapper extends HttpClient {
  constructor(
    private httpHandler: HttpHandler,
    private log: NGXLogger
  ) {
    super(httpHandler);
  }

  /**
   * Use the given device for the request.
   *
   * If the device is left empty, the active device is used (the request will not be modified).
   *
   * ```typescript
   * // Request to https://example.com/api/user/my-endpoint
   * this.scgHttpWrapper.withDevice('abc').get('https://example.com/api/user/my-endpoint')
   * ```
   */
  public withDevice(deviceId?: string): ScgHttpWrapper {
    return this.addInterceptor({
      intercept(req, next) {
        // Set device
        if (deviceId != null && deviceId !== '') {
          req = req.clone({
            headers: req.headers.set('X-pcid', deviceId)
          });
        }

        return next.handle(req);
      }
    });
  }

  /**
   * Use the given username for the request.
   *
   * If no username is given, the request will not be modified.
   *
   * apiUrl is the part before the username, the original request's URL is the part after the username.
   *
   * ```typescript
   * // Request to https://example.com/api/user/my-endpoint (apiUrl is optional and will default to the default apiUrl)
   * this.scgHttpWrapper.withUsername('username', 'https://example.com/api').get('my-endpoint')
   * ```
   *
   * @param apiUrl If not given, it defaults to the default apiUrl
   */
  public withUsername(username?: string, apiUrl?: string): ScgHttpWrapper {
    const buildUserUrl = this.buildUserUrl;

    return this.addInterceptor({
      intercept(req, next) {
        // Set username
        if (username != null && username !== '') {
          req = req.clone({
            url: buildUserUrl(apiUrl ?? environment.apiUrl, username, req.url)
          });
        }

        return next.handle(req);
      }
    });
  }

  /**
   * Does not intercept errors with the given status codes.
   * If no status codes are given, all HTTP errors are not intercepted.
   */
  public withoutErrorInterception(...statusCodesToIgnore: number[]): ScgHttpWrapper {
    return this.addInterceptor({
      intercept(req, next) {
        // Add custom header to tell the error interceptor which status codes should be ignored
        req = req.clone({
          headers: req.headers.set('X-interceptor-error-status-codes-to-ignore', statusCodesToIgnore.join(','))
        });

        return next.handle(req);
      }
    });
  }

  /**
   * Do not trigger device sync
   */
  public withoutSync() {
    return this.addInterceptor({
      intercept(req, next) {
        // Add custom header to notify the need-sync interceptor not to trigger sync
        req = req.clone({
          headers: req.headers.set('X-interceptor-nosync', 'true')
        });

        return next.handle(req);
      }
    });
  }

  private addInterceptor(interceptor: HttpInterceptor): ScgHttpWrapper {
    const handler = new HttpInterceptorHandler(this.httpHandler, interceptor);
    return new ScgHttpWrapper(handler, this.log);
  }

  /**
   * Example:
   * ```typescript
   * // Returns 'https://portal.salfeld.de/api/user/myrequest'
   * buildUserUrl('https://portal.salfeld.de/api/', 'user', '/myrequest');
   * ```
   */
  private buildUserUrl(apiUrl: string, username: string, path: string) {
    let url = apiUrl;

    if (url.charAt(url.length - 1) !== '/') {
      url += '/';
    }

    url += username + '/';

    if (path.charAt(0) === '/') {
      path = path.substring(1);
    }

    return url + path;
  }
}

class HttpInterceptorHandler implements HttpHandler {
  constructor(private next: HttpHandler, private interceptor: HttpInterceptor) {}

  public handle(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    return this.interceptor.intercept(req, this.next);
  }
}
